import type { editor } from "monaco-editor";
import editorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker";
import cssWorker from "monaco-editor/esm/vs/language/css/css.worker?worker";
import htmlWorker from "monaco-editor/esm/vs/language/html/html.worker?worker";
import jsonWorker from "monaco-editor/esm/vs/language/json/json.worker?worker";
import tsWorker from "monaco-editor/esm/vs/language/typescript/ts.worker?worker";
import { storeToRefs } from "pinia";
import { Ref, watch } from "vue";
import { useUser } from "../composition-functions/user";
import { ApplicationTheme } from "../enums/ApplicationTheme";
import { getApplicationTheme } from "./themeUtils";

// @ts-ignore Fix MonacoEnvironment is not defined error
self.MonacoEnvironment = {
  getWorker(_: unknown, label: string) {
    if (label === "json") {
      return new jsonWorker();
    }
    if (label === "css" || label === "scss" || label === "less") {
      return new cssWorker();
    }
    if (label === "html" || label === "handlebars" || label === "razor") {
      return new htmlWorker();
    }
    if (label === "typescript" || label === "javascript") {
      return new tsWorker();
    }
    return new editorWorker();
  },
};

interface CreateMonacoEditorInstanceParams {
  editorElement: Ref<HTMLElement>;
  editorOptions?: editor.IStandaloneEditorConstructionOptions;
  editorValue: string;
  editorLanguage: "html" | "javascript" | "json" | "markdown" | "scss";
  onChange?: (value: string) => void;
}
export async function createMonacoEditorInstance({
  editorElement,
  editorOptions,
  editorValue,
  editorLanguage,
  onChange,
}: CreateMonacoEditorInstanceParams) {
  const userStore = useUser();
  const { applicationTheme } = storeToRefs(userStore);

  const monaco = await import("monaco-editor");

  const monacoEditor = monaco.editor.create(editorElement.value, {
    ...editorOptions,
    automaticLayout: true,
    tabSize: 2,
    minimap: {
      enabled: false,
    },
    theme: getApplicationTheme(applicationTheme.value) === ApplicationTheme.DARK ? "vs-dark" : "vs-light",
    language: editorLanguage,
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
    value: editorValue,
  });

  monacoEditor.getModel()?.updateOptions({ tabSize: 2 });

  if (onChange !== undefined) {
    monacoEditor.onDidChangeModelContent(() => {
      onChange(monacoEditor.getValue());
    });
  }

  watch(applicationTheme, () => {
    monaco.editor.setTheme(getApplicationTheme(applicationTheme.value) === ApplicationTheme.DARK ? "vs-dark" : "vs-light");
  });

  return monacoEditor;
}

interface CreateMonacoDiffEditorInstanceParams {
  editorElement: Ref<HTMLElement>;
  editorOptions?: editor.IStandaloneDiffEditorConstructionOptions;

  oldData: null | JsonObject;

  newData: null | JsonObject;
}

export async function createMonacoDiffEditorInstance({
  editorElement,
  editorOptions,
  newData,
  oldData,
}: CreateMonacoDiffEditorInstanceParams) {
  const userStore = useUser();
  const { applicationTheme } = storeToRefs(userStore);

  const monaco = await import("monaco-editor");

  const oldModel = monaco.editor.createModel(JSON.stringify(oldData, null, "  "), "json");
  const newModel = monaco.editor.createModel(JSON.stringify(newData, null, "  "), "json");

  const monacoDiffEditor = monaco.editor.createDiffEditor(editorElement.value, {
    ...editorOptions,
    automaticLayout: true,
    minimap: {
      enabled: false,
    },
    theme: getApplicationTheme(applicationTheme.value) === ApplicationTheme.DARK ? "vs-dark" : "vs-light",
    scrollbar: {
      alwaysConsumeMouseWheel: true,
    },
    readOnly: true,
    renderSideBySide: true,
    lineNumbers: "off",
  });

  monacoDiffEditor.setModel({
    original: oldModel,
    modified: newModel,
  });

  watch(applicationTheme, () => {
    monaco.editor.setTheme(getApplicationTheme(applicationTheme.value) === ApplicationTheme.DARK ? "vs-dark" : "vs-light");
  });

  return monacoDiffEditor;
}
